/**
 * Biome colour tints (grass, foliage, water).
 *
 * Vanilla's BiomeColors.getAverageColor samples each block's biome (a single
 * biome per column, since the noise sampler is snapped to quart positions)
 * and averages that biome's grass/foliage/water colour over a configurable
 * radius (Off = 1x1, Fast = 3x3, Fancy = 5x5). Blocks with their own fixed
 * tint (spruce, birch, ...) keep BlockDef.tint.
 *
 * DOM-free: shared by the mesh worker and the main thread.
 */

import {
  BLOCKS, T, TILE_COUNT, tintOf, TINTABLE_TILES, ACACIA_LEAVES_ID, DARK_OAK_LEAVES_ID, FERN_ID, GRASS_ID,
  JUNGLE_LEAVES_ID, LEAVES_ID, MANGROVE_LEAVES_ID, SUGAR_CANE_ID, TALL_GRASS_ID, WATER_ID,
} from './blocks';
import { CHUNK_SIZE } from './constants';
import { Biome, type TerrainGenerator } from './worldgen/terrain';
import { BIOME_LIST } from './worldgen/biomes';

export const enum TintKind {
  Fixed = 0,   // BlockDef.tint as-is (spruce, birch, ...) or none
  Grass = 1,
  Foliage = 2,
  Water = 3,
}

/** Tint kind per block id. */
export const TINT_KIND: Uint8Array = new Uint8Array(BLOCKS.length);
for (const id of [GRASS_ID, TALL_GRASS_ID, FERN_ID, SUGAR_CANE_ID]) TINT_KIND[id] = TintKind.Grass;
for (const id of [LEAVES_ID, JUNGLE_LEAVES_ID, ACACIA_LEAVES_ID, DARK_OAK_LEAVES_ID, MANGROVE_LEAVES_ID]) {
  TINT_KIND[id] = TintKind.Foliage;
}
TINT_KIND[WATER_ID] = TintKind.Water;

/**
 * Tiles whose tint applies only to alpha-masked pixels (the grass strip on a
 * grass block's side; see render/atlasOverlays.ts).
 */
export const MASKED_TINT_TILES: Uint8Array = new Uint8Array(TILE_COUNT);
MASKED_TINT_TILES[T.GRASS_SIDE] = 1;

/** Vanilla colours per biome id: [grass, foliage, water] as 0xRRGGBB (sRGB). */
const BIOME_COLORS: ReadonlyArray<[number, number, number]> = BIOME_LIST.map((b) => [b.grass, b.foliage, b.water]);
const FALLBACK = BIOME_COLORS[Biome.Plains];

/** Vanilla biome blend radius (cells on each side of a block). 0 = 1x1, 2 = 5x5. */
export type BlendRadius = 0 | 1 | 2;
let BLEND: BlendRadius = 2;
export function setBlendRadius(r: BlendRadius): void { BLEND = r; }
export function blendRadius(): BlendRadius { return BLEND; }

const GRID = CHUNK_SIZE + 4 * 2; // enough padding for the largest radius (2)

/** Per-column tints of one chunk: packed 0xRRGGBB per kind, index x + z * 16. */
export interface ChunkTints {
  grass: Int32Array;
  foliage: Int32Array;
  water: Int32Array;
}

/** Uniform plains tints, for callers without a terrain generator. */
export function defaultChunkTints(): ChunkTints {
  const n = CHUNK_SIZE * CHUNK_SIZE;
  return {
    grass: new Int32Array(n).fill(FALLBACK[0]),
    foliage: new Int32Array(n).fill(FALLBACK[1]),
    water: new Int32Array(n).fill(FALLBACK[2]),
  };
}

const biomeGridUnused: Uint8Array = new Uint8Array(GRID * GRID);
void biomeGridUnused;

/** Uniform plains tints, allocated once for callers that have no position. */
const PLAINS_TINTS = defaultChunkTints();

/**
 * sRGB 0xRRGGBB that a HUD icon of block `id` showing `tile` must be
 * multiplied by, or null when the tile is already fully coloured. Mirrors the
 * mesher's `tintAt` but samples the uniform plains tints — the HUD has no
 * world position to blend a biome tint over.
 *
 * Water returns null: the voxel atlas' water tile is pre-multiplied by its
 * fixed colour in buildAtlas (see TILE_TINTS), so tinting again would double it.
 */
export function iconTint(id: number, tile: number): number | null {
  const kind = TINT_KIND[id];
  if (kind === TintKind.Water) return null;
  if (TINTABLE_TILES[tile] !== 1 && MASKED_TINT_TILES[tile] !== 1) return null;
  if (kind === TintKind.Fixed) {
    const t = tintOf(id);
    return (Math.round(t[0] * 255) << 16) | (Math.round(t[1] * 255) << 8) | Math.round(t[2] * 255);
  }
  const map = kind === TintKind.Grass ? PLAINS_TINTS.grass : PLAINS_TINTS.foliage;
  return map[0];
}

/**
 * Biome-blended tints for every column of chunk (cx, cz). Mirrors vanilla's
 * BiomeColors.getAverageColor: the noise sampler returns one biome per
 * column (it's snapped to quart positions internally), so we sample one
 * biome per block, sum colours in sRGB, and round.
 *
 * A grid 8 cells wider than the chunk covers the (2*BLEND+1) window plus
 * some safety margin so neighbouring chunks can read it.
 */
export function computeChunkTints(gen: TerrainGenerator, cx: number, cz: number): ChunkTints {
  const radius = BLEND;
  const N = radius * 2 + 1;
  // anchor the sample grid so every chunk reads the same cells at the same
  // (block, chunk-relative) positions. Vanilla does the same: each block gets
  // a fresh window starting at (blockX - radius).
  const ox = cx * CHUNK_SIZE - radius;
  const oz = cz * CHUNK_SIZE - radius;
  const W = CHUNK_SIZE + radius * 2;
  const H = CHUNK_SIZE + radius * 2;
  const bios = new Uint8Array(W * H);
  let uniform = true;
  for (let z = 0, i = 0; z < H; z++) {
    for (let x = 0; x < W; x++, i++) {
      bios[i] = gen.columnInfo(ox + x, oz + z).biome;
      if (bios[i] !== bios[0]) uniform = false;
    }
  }
  const out = defaultChunkTints();
  if (uniform) {
    const c = BIOME_COLORS[bios[0]] ?? FALLBACK;
    out.grass.fill(c[0]);
    out.foliage.fill(c[1]);
    out.water.fill(c[2]);
    return out;
  }
  const kinds = [out.grass, out.foliage, out.water];
  const count = N * N;
  for (let z = 0; z < CHUNK_SIZE; z++) {
    for (let x = 0; x < CHUNK_SIZE; x++) {
      for (let k = 0; k < 3; k++) {
        let r = 0, g = 0, b = 0;
        for (let dz = 0; dz < N; dz++) {
          for (let dx = 0; dx < N; dx++) {
            const c = (BIOME_COLORS[bios[(x + dx) + (z + dz) * W]] ?? FALLBACK)[k];
            r += c >> 16;
            g += (c >> 8) & 0xff;
            b += c & 0xff;
          }
        }
        kinds[k][x + z * CHUNK_SIZE] =
          (Math.round(r / count) << 16) | (Math.round(g / count) << 8) | Math.round(b / count);
      }
    }
  }
  return out;
}
